import { ADMIN_PATH } from './adminPath';
import { EVENT_PING_PARAM_KEYS } from './eventPingParams.js';
import { PROVIDERS_SEGMENT } from './providerRoutes.js';
import type { EventPingBody, EventPingRow, RefInfo } from '../types/api/eventPing';
import type { ViewportBucket } from '../types/api/siteStats';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const hasGtag = (): boolean => typeof window !== 'undefined' && typeof window.gtag === 'function';

export function setAnalyticsConsent(granted: boolean): void {
  if (!hasGtag()) return;
  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
  });
}

export function setAdConsent(granted: boolean): void {
  if (!hasGtag()) return;
  const value = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
}

export function trackPageview(path: string): void {
  if (!hasGtag()) return;
  // Path + location snapshot now; the TITLE read is deferred two frames.
  // This tracker's effect runs before the destination page's useSEO effect
  // sets document.title, so a synchronous read reports the PREVIOUS page's
  // title on every SPA navigation.
  const pageLocation = window.location.href;
  const send = (): void => {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: pageLocation,
      page_title: document.title,
    });
  };
  // rAF is suspended in hidden tabs — a navigation followed by tab-hide would
  // stall the page_view (and lose it on close). Hidden tab: send immediately
  // and accept the possibly-stale title for that rare case.
  if (typeof requestAnimationFrame === 'function' && document.visibilityState !== 'hidden') {
    requestAnimationFrame(() => requestAnimationFrame(send));
  } else {
    send();
  }
}

// The admin panel emits no analytics, ever — enforced here instead of at each
// call site so shared components (content blocks, previews) can track freely
// without knowing whether they're rendered inside the admin.
const onAdminPage = (): boolean =>
  typeof window !== 'undefined' && window.location.pathname.startsWith(ADMIN_PATH);

export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (onAdminPage()) return;
  const data = params ?? {};
  if (hasGtag()) window.gtag('event', name, data);
  // The site's own event stream (admin Statistieken tab). Batched so an active
  // session costs one small POST every few seconds at most. GA4 needs no
  // queue of its own: the inline gtag stub queues to dataLayer from the first
  // byte.
  queueEventPing(name, data);
}

export function trackAdsConversion(sendTo: string, params?: Record<string, unknown>): void {
  if (!hasGtag() || !sendTo) return;
  window.gtag('event', 'conversion', { send_to: sendTo, ...(params ?? {}) });
}

// ─── Tagging taxonomy ─────────────────────────────────────────────────────
// One source of truth for HOW we tag items / pages / events / clicks-out so
// the same dimensions show up consistently in GA4 and the first-party stream. Older code
// mixed `source` and `surface`; everything now standardises on:
//   surface  — WHERE in the UI the interaction happened (a SURFACES value)
//   variant  — which flavour of that surface, when one surface has several
//              renderings (nav: desktop/mobile, cookie consent: wall/banner)
//   language — always stamped, on every event
// Helpers below emit the same param shape per event type on every surface.

/**
 * Canonical `surface` values — the single dimension that answers "where in the
 * UI did this happen". Pass one of these on every interaction event.
 */
export const SURFACES = {
  comparisonTable: 'comparison_table',
  resultsBar: 'results_bar',
  filters: 'filters',
  providerProfile: 'provider_profile',
  headToHead: 'head_to_head',
  multiCompare: 'multi_compare',
  neaProviders: 'nea_providers',
  midCheckPage: 'mid_check_page',
  midCheckerModal: 'mid_checker_modal',
  // The laadpalen catalog: the brand hub (/laadpalen — its search) and a
  // brand page (/laadpalen/<merk> — its "Bekijk bij Laadpaaldirect" links). The brand
  // page's surface names the UI like midCheckPage does; its page category in
  // the outbound ledger is `charger_brand` (pageCategoryForPath).
  chargerBrands: 'charger_brands',
  chargerBrandPage: 'charger_brand_page',
  smartChargerModal: 'smart_charger_modal',
  noChargerLanding: 'no_charger_landing',
  header: 'header',
  footer: 'footer',
  cookieBanner: 'cookie_banner',
  partnerLanding: 'partner_landing',
  partnerLogin: 'partner_login',
  contactPage: 'contact_page',
  article: 'article',
  glossary: 'glossary',
  glossaryTerm: 'glossary_term',
  knowledgeBase: 'knowledge_base',
  floatingCta: 'floating_cta',
  notFound: 'not_found',
  hero: 'hero',
  signupForm: 'signup_form',
} as const;
export type Surface = (typeof SURFACES)[keyof typeof SURFACES];

/**
 * Shared context for any provider-scoped event (signup click-out, card expand,
 * profile view, review-badge click). `slug` is the universal join key (present
 * on every surface); `id` is included only where the calling surface has it
 * cheaply. Every other field is optional and dropped when empty.
 */
export interface ProviderEventContext {
  id?: string | number | null;
  name?: string | null;
  slug?: string | null;
  /** 1-based rank within the filtered + sorted result set, across pages. */
  position?: number;
  /** 1-based results page the item sat on when the user acted. */
  page?: number;
  /** commission bucket: 'low' | 'medium' | 'high' | 'unknown'. */
  commissionTier?: string | null;
  contractId?: string | number | null;
}

const isPresent = (v: unknown): boolean => v !== undefined && v !== null && v !== '';

/**
 * Build a consistent param object for provider-scoped events. Always stamps
 * `surface` + `language`; omits any empty field so payloads stay lean and the
 * same keys mean the same thing across every surface.
 */
export function providerEventParams(
  ctx: ProviderEventContext,
  surface: Surface | string,
  language: string
): Record<string, unknown> {
  const out: Record<string, unknown> = { surface, language };
  if (isPresent(ctx.id)) out.provider_id = ctx.id;
  if (isPresent(ctx.name)) out.provider_name = ctx.name;
  if (isPresent(ctx.slug)) out.provider_slug = ctx.slug;
  if (isPresent(ctx.position)) out.position = ctx.position;
  if (isPresent(ctx.page)) out.page = ctx.page;
  if (isPresent(ctx.commissionTier)) out.commission_tier = ctx.commissionTier;
  if (isPresent(ctx.contractId)) out.contract_id = ctx.contractId;
  return out;
}

const SIGNUP_AW_SEND_TO = import.meta.env.VITE_AW_SIGNUP_SEND_TO as string | undefined;

/**
 * The primary click-out: user taps a provider's signup CTA to leave for the
 * provider's site. Fans out to GA4 and the first-party stream with a consistent provider payload
 * (so "clicks-out by provider / surface / rank" reports work on every page) and
 * fires the Google Ads conversion when `VITE_AW_SIGNUP_SEND_TO` is set.
 */
export function trackProviderSignupClick(
  ctx: ProviderEventContext & { signupUrl?: string | null },
  surface: Surface | string,
  language: string
): void {
  const params = providerEventParams(ctx, surface, language);
  if (isPresent(ctx.signupUrl)) params.signup_url = String(ctx.signupUrl).slice(0, 100);
  trackEvent('provider_signup_click', params);
  if (SIGNUP_AW_SEND_TO) trackAdsConversion(SIGNUP_AW_SEND_TO, { surface });
}

/** Where the secondary website link of a provider with the in-site form sits. */
export type ProviderWebsiteVariant = 'panel' | 'profile_header' | 'confirm_page';

/**
 * The SECONDARY click-out (2026-10-23): a provider that takes aanmeldingen
 * through our own form keeps the form as its primary action, and gets a muted
 * link to its own website beside it — the comparer panel's foot (the phone card
 * included), the profile header, and the confirm page after the double opt-in.
 * Deliberately its own event and never `provider_signup_click`: that one means
 * "the primary CTA sent the visitor to the provider", counts as a signup in the
 * first-party stream and fires the Ads conversion; this one does neither.
 */
export function trackProviderWebsiteClick(
  ctx: ProviderEventContext,
  surface: Surface | string,
  language: string,
  variant: ProviderWebsiteVariant
): void {
  trackEvent('provider_website_click', {
    ...providerEventParams(ctx, surface, language),
    variant,
  });
}

/** High-intent internal drill-in: user opens a provider's full profile page. */
export function trackViewProfileClick(
  ctx: ProviderEventContext,
  surface: Surface | string,
  language: string
): void {
  trackEvent('view_profile_click', providerEventParams(ctx, surface, language));
}

/**
 * Split the language prefix off a pathname. Mirrors the router's convention
 * (`/en/...` and `/nl/...`, bare paths default to nl). Shared by the SPA
 * page-view tracker and the outbound-click listener so both derive identical
 * `language` + category inputs.
 */
export function splitLanguageFromPath(pathname: string): { language: string; path: string } {
  const langMatch = pathname.match(/^\/(en|nl)(?=\/|$)/);
  return {
    language: langMatch ? langMatch[1] : 'nl',
    path: pathname.replace(/^\/(en|nl)(?=\/|$)/, '') || '/',
  };
}

/**
 * Surface for components that render on more than one page (CMS content
 * blocks, shared widgets): the current page's category. SSR-safe — returns
 * 'other' when there's no window (events never fire server-side anyway).
 */
export function currentPageSurface(): string {
  if (typeof window === 'undefined') return 'other';
  const { path } = splitLanguageFromPath(window.location.pathname);
  return pageCategoryForPath(path) ?? 'other';
}

// --- First-party event stream -----------------------------------------------
// Every trackEvent is also queued for the site's own analytics (the admin
// Statistieken tab). Batched: one POST per ~5 s of activity, flushed early on
// a full batch and on pagehide/tab-hide so closing the tab loses nothing.
// The payload carries no identifiers — sessions are derived server-side from
// a daily-rotating salted hash (see server/data/siteEvents.ts). Body:
// `{ events, ref?, vp? }` — ref once per page load, vp (the viewport-width
// bucket, see viewportBucket) on every batch. Its shape is ONE definition with
// the route that reads it: EventPingBody in src/types/api/eventPing.ts.

/** The page a row is filed under: its `p` (language-stripped path), `s`, `l`. */
interface PingPlace {
  path: string;
  surface: string;
  lang: string;
}

let eventQueue: EventPingRow[] = [];
let eventFlushTimer: ReturnType<typeof setTimeout> | null = null;
const EVENT_FLUSH_MS = 5000;
const EVENT_BATCH_MAX = 25;

// Params the first-party stream keeps (key → max string length) — the ONE
// list the server keeps too (src/lib/eventPingParams.js); everything else is
// dropped, so the payload stays identifier-free. Re-exported for the admin's
// free-query form and its test.
export { EVENT_PING_PARAM_KEYS };

function pickPingParams(
  params: Record<string, unknown>
): Record<string, string | number> | undefined {
  let out: Record<string, string | number> | undefined;
  for (const [key, max] of Object.entries(EVENT_PING_PARAM_KEYS)) {
    const v = params[key];
    if (typeof v === 'string' && v) {
      let s = v.slice(0, max);
      if (key === 'slugs' && v.length > max) {
        // Drop the partially-cut trailing slug rather than corrupting it.
        const cut = s.lastIndexOf(',');
        if (cut > 0) s = s.slice(0, cut);
      }
      (out ??= {})[key] = s;
    } else if (typeof v === 'number' && Number.isFinite(v)) {
      (out ??= {})[key] = v;
    }
  }
  return out;
}

// Referrer/UTM context is meaningful once per page LOAD (SPA navigations keep
// the session alive). Snapshotted at the FIRST queued event, not at flush
// time: the flush runs up to 5 s later, and a paid visitor who clicks through
// to another route inside that window would have lost ?gclid/utm_* to the SPA
// navigation. Attached to the first flushed batch so the server can attribute
// the session's acquisition channel (see server/data/siteEvents).
let bootRef: RefInfo | null = null;
let bootRefCaptured = false;
let bootRefSent = false;

function captureBootRef(): void {
  if (bootRefCaptured) return;
  bootRefCaptured = true;
  try {
    let r = document.referrer || '';
    try {
      // A same-host referrer is an internal reload, not an acquisition source.
      if (r && new URL(r).host === window.location.host) r = '';
    } catch {
      r = '';
    }
    const q = new URLSearchParams(window.location.search);
    const ref: RefInfo = { r: r.slice(0, 300) };
    const us = q.get('utm_source');
    const um = q.get('utm_medium');
    const uc = q.get('utm_campaign');
    if (us) ref.us = us.slice(0, 80);
    if (um) ref.um = um.slice(0, 80);
    if (uc) ref.uc = uc.slice(0, 100);
    if (q.has('gclid') || q.has('gbraid') || q.has('wbraid')) ref.g = true;
    bootRef = ref;
  } catch {
    bootRef = { r: '' };
  }
}

/**
 * Where a row about `pathname` is filed: the language-stripped path, the
 * caller's `surface` or else the page category (what `key_page_view` gets),
 * and the language from the URL prefix.
 */
function pingPlaceFor(pathname: string, surface?: unknown): PingPlace {
  const { language, path } = splitLanguageFromPath(pathname);
  return {
    path,
    surface:
      typeof surface === 'string' && surface ? surface : (pageCategoryForPath(path) ?? 'other'),
    lang: language,
  };
}

/**
 * Queue one row for the first-party stream. `at` files it under another page
 * than the current address — for a row that is ABOUT a page the visitor has
 * just left (page_leave on a route change, when window.location already shows
 * the next one). Path, language and surface all follow it, so
 * the row never reads as if it happened on the page that merely triggered it.
 */
function queueEventPing(name: string, params: Record<string, unknown>, at?: PingPlace): void {
  if (typeof window === 'undefined') return;
  try {
    captureBootRef();
    const place = at ?? pingPlaceFor(window.location.pathname, params.surface);
    const row: EventPingRow = {
      t: Date.now(),
      e: name.slice(0, 50),
      p: (place.path || '/').slice(0, 100),
      s: String(place.surface).slice(0, 40),
      l: place.lang,
    };
    const x = pickPingParams(params);
    if (x) row.x = x;
    eventQueue.push(row);
    if (eventQueue.length >= EVENT_BATCH_MAX) {
      flushEventQueue();
    } else if (eventFlushTimer === null) {
      eventFlushTimer = setTimeout(flushEventQueue, EVENT_FLUSH_MS);
    }
  } catch {
    /* tracking must never break the interaction */
  }
}

// Chrome can speculatively PRERENDER a page (omnibox/search predictions); the
// full app runs there, so the flush timer would beacon before the user ever
// activates the page. The server drops those requests (Sec-Purpose header,
// server/middleware/beaconFilter.ts) — correct for never-activated prerenders,
// but a send from a prerendered page would still latch bootRefSent and burn
// the once-per-load ref. So: hold the queue while prerendering and flush on
// activation; a never-activated prerender then simply sends nothing.
let prerenderFlushArmed = false;

function isPrerendering(): boolean {
  return (
    typeof document !== 'undefined' &&
    (document as Document & { prerendering?: boolean }).prerendering === true
  );
}

/**
 * The viewport-width bucket every batch carries as `vp`, on the site's own
 * (Tailwind) breakpoints — xs and sm are phones (`max-width: 767px`). A class
 * of screen, never a raw dimension; undefined when the width can't be read.
 */
function viewportBucket(): ViewportBucket | undefined {
  try {
    const w = window.innerWidth;
    if (!Number.isFinite(w) || w <= 0) return undefined;
    if (w < 640) return 'xs';
    if (w < 768) return 'sm';
    if (w < 1024) return 'md';
    if (w < 1280) return 'lg';
    return 'xl';
  } catch {
    return undefined;
  }
}

function flushEventQueue(): void {
  if (eventFlushTimer !== null) {
    clearTimeout(eventFlushTimer);
    eventFlushTimer = null;
  }
  if (!eventQueue.length) return;
  if (isPrerendering()) {
    if (!prerenderFlushArmed) {
      prerenderFlushArmed = true;
      document.addEventListener('prerenderingchange', () => flushEventQueue(), { once: true });
    }
    return;
  }
  const batch = eventQueue;
  eventQueue = [];
  try {
    let ref: RefInfo | null = null;
    if (!bootRefSent) {
      bootRefSent = true;
      ref = bootRef ?? { r: '' };
    }
    const payload: EventPingBody = {
      events: batch,
    };
    if (ref) payload.ref = ref;
    const vp = viewportBucket();
    if (vp) payload.vp = vp;
    const body = JSON.stringify(payload);
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // sendBeacon returns false when the UA refuses to queue the payload
      // (in-flight beacon quota) — fall through to fetch instead of silently
      // dropping the batch (and, worst case, the session's ref attribution).
      if (navigator.sendBeacon('/api/event-ping', new Blob([body], { type: 'application/json' }))) {
        return;
      }
    }
    void fetch('/api/event-ping', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* best effort */
  }
}

// --- Engagement marker (first-party only) -----------------------------------
// `page_engaged` is queued once per pageview after the page has been in the
// FOREGROUND for ENGAGED_AFTER_MS — GA4's engaged-session floor. It exists so
// the first-party session stats can tell a bounce (gone within 10 s, touched
// nothing) from a visitor who stayed and read without clicking: without it
// the only timestamp a single-page session has is the pageview itself.
// Hidden time doesn't count — a tab opened in the background and never looked
// at is not engaged — and a marker that would land outside its own session
// window (tab resumed much later) is dropped rather than opening a phantom
// session. Deliberately NOT routed through trackEvent: GA4 has its own
// engagement model and would just gain a noise event per pageview.
export const ENGAGED_AFTER_MS = 10_000;
// Comfortably inside the server's 30-min session gap, so a late marker can
// never start a session of its own.
const ENGAGED_MAX_WALL_MS = 25 * 60 * 1000;
let engagedTimer: ReturnType<typeof setTimeout> | null = null;
let engagedRemainingMs = 0;
let engagedVisibleSince = 0;
let engagedArmedAt = 0;
let engagedListening = false;

const pageIsVisible = (): boolean =>
  typeof document !== 'undefined' && document.visibilityState !== 'hidden';

function stopEngagedTimer(): void {
  if (engagedTimer !== null) {
    clearTimeout(engagedTimer);
    engagedTimer = null;
  }
}

function fireEngaged(): void {
  engagedTimer = null;
  engagedRemainingMs = 0;
  if (onAdminPage()) return;
  queueEventPing('page_engaged', {});
}

function resumeEngagedTimer(): void {
  if (engagedRemainingMs <= 0 || engagedTimer !== null || !pageIsVisible()) return;
  if (Date.now() - engagedArmedAt > ENGAGED_MAX_WALL_MS) {
    engagedRemainingMs = 0;
    return;
  }
  engagedVisibleSince = Date.now();
  engagedTimer = setTimeout(fireEngaged, engagedRemainingMs);
}

function pauseEngagedTimer(): void {
  if (engagedTimer === null) return;
  stopEngagedTimer();
  engagedRemainingMs = Math.max(0, engagedRemainingMs - (Date.now() - engagedVisibleSince));
}

// One visibility listener for both clocks of a pageview: the 10 s engagement
// marker and the page visit page_leave reports on.
function onPageVisibilityChange(): void {
  if (pageIsVisible()) {
    resumeEngagedTimer();
    resumeVisitClock();
  } else {
    pauseEngagedTimer();
    pauseVisitClock();
  }
}

// --- Page visit: time on page + scroll depth (first-party only) -------------
// `page_leave` closes a page visit — one key_page_view up to the next
// navigation — with how long the page was in the FOREGROUND (`seconds`) and
// how far down it was seen (`percent`: 0/25/50/75/100). It is queued when the
// tab is hidden and on pagehide (both ahead of the flush that follows), and
// when an SPA navigation leaves the page. It is the ONE exit marker since
// 2026-10-02: the partner portal's profile dwell reads it too (the separate
// once-per-pageview `page_dwell` row it replaced lost a phone tab killed
// without a pagehide, and stopped counting at the first hide). The values are
// cumulative for the visit, so a tab hidden twice sends two rows and the
// larger is the answer; an unchanged payload is not sent again. Each row is
// filed under the page it closes, because at an SPA leave window.location
// already shows the next one. First-party only for the same reasons as
// page_engaged. Unlike the marker it is sent however late it comes: the server
// never opens a session (or counts a visitor) for a batch of nothing but leave
// reports, and sessionize files a late one under the visit it reports on — so
// a long passive read without a click keeps its time.
//
// Capped at 20 minutes of foreground time: a tab left in the foreground and
// forgotten says nothing about reading, and one such tab must not drag an
// average up. The server clamps to the same number (siteEvents.ts).
export const LEAVE_MAX_SECONDS = 1200;
// A page that scrolls less than this has no meaningful depth: "read to the
// end" would be true of everyone who saw it.
const LEAVE_MIN_SCROLLABLE_PX = 400;
// scrollHeight is a whole number while scrollY can be fractional (zoom, device
// pixel ratio), so the very bottom of a page can read a pixel short of it.
const LEAVE_BOTTOM_SLACK_PX = 2;

interface PageVisit extends PingPlace {
  /** The raw pathname the visit was armed on. */
  pathname: string;
  visibleMs: number;
  visibleSince: number | null;
  /** Deepest point seen, in px from the top of the page: scrollY + innerHeight. */
  maxReach: number;
  /** The page's own height, last measured while it was on screen (0 = never). */
  docHeight: number;
  lastSent: string | null;
}

let pageVisit: PageVisit | null = null;

/**
 * The open visit, while the address still shows its page. An SPA navigation
 * changes the URL before the next page renders, so from that moment on the
 * document is (or is about to be) the next page and nothing may be sampled
 * into this visit — its leave then uses what was measured while it showed.
 */
function visitOnScreen(): PageVisit | null {
  const v = pageVisit;
  return v && typeof window !== 'undefined' && window.location.pathname === v.pathname ? v : null;
}

/** How far down the page has been seen. Runs on scroll: no height read here. */
function sampleVisitReach(): void {
  const v = visitOnScreen();
  if (!v) return;
  try {
    const reach = window.scrollY + window.innerHeight;
    if (reach > v.maxReach) v.maxReach = reach;
  } catch {
    /* measuring must never break the page */
  }
}

/** The page's current height — it grows as content loads, shrinks as panels close. */
function sampleVisitHeight(): void {
  const v = visitOnScreen();
  if (!v) return;
  try {
    const height = document.documentElement.scrollHeight;
    if (height > 0) v.docHeight = height;
  } catch {
    /* measuring must never break the page */
  }
}

function visitVisibleMs(v: PageVisit, now: number): number {
  return v.visibleMs + (v.visibleSince !== null ? Math.max(0, now - v.visibleSince) : 0);
}

function pauseVisitClock(): void {
  const v = pageVisit;
  if (!v || v.visibleSince === null) return;
  v.visibleMs = visitVisibleMs(v, Date.now());
  v.visibleSince = null;
}

/**
 * The tab is on screen again: the same visit counts on, however long it was
 * away — its next report still belongs to it (see LEAVE_MAX_SECONDS).
 */
function resumeVisitClock(): void {
  const v = pageVisit;
  if (!v || v.visibleSince !== null || !pageIsVisible()) return;
  v.visibleSince = Date.now();
}

/** The deepest quarter of the page seen; undefined on a page that barely scrolls. */
function visitPercent(v: PageVisit): number | undefined {
  if (!(v.docHeight - window.innerHeight >= LEAVE_MIN_SCROLLABLE_PX)) return undefined;
  if (v.maxReach >= v.docHeight - LEAVE_BOTTOM_SLACK_PX) return 100;
  return Math.min(100, Math.floor((v.maxReach / v.docHeight) * 4) * 25);
}

/**
 * Queue the open visit's page_leave. Safe to call on every hide: the values
 * are cumulative, and a payload equal to the last one sent is skipped.
 */
function queuePageLeave(): void {
  const v = pageVisit;
  if (!v || typeof window === 'undefined' || onAdminPage()) return;
  try {
    // A last look while the page is still on screen (tab hide, pagehide); at
    // an SPA leave both are no-ops and the samples taken while it showed stand.
    sampleVisitReach();
    sampleVisitHeight();
    const now = Date.now();
    const seconds = Math.min(LEAVE_MAX_SECONDS, Math.round(visitVisibleMs(v, now) / 1000));
    const percent = visitPercent(v);
    const payload = `${seconds}|${percent ?? ''}`;
    if (payload === v.lastSent) return;
    v.lastSent = payload;
    queueEventPing('page_leave', percent === undefined ? { seconds } : { seconds, percent }, v);
  } catch {
    /* tracking must never break the page */
  }
}

/** Open the visit for the page on screen now — its key_page_view just fired. */
function startPageVisit(): void {
  const now = Date.now();
  const pathname = window.location.pathname;
  pageVisit = {
    ...pingPlaceFor(pathname),
    pathname,
    visibleMs: 0,
    visibleSince: pageIsVisible() ? now : null,
    maxReach: 0,
    docHeight: 0,
    lastSent: null,
  };
  sampleVisitReach();
  sampleVisitHeight();
}

/**
 * (Re)start the engagement clock — and the visit page_leave reports on — for
 * the pageview that just fired. Called by the SPA pageview tracker for every
 * categorised route. A pageview still open from the previous page is closed
 * first (its page_leave); a navigation before the marker fires simply restarts
 * the clock for the new page, so at most one marker per pageview.
 */
export function armPageEngaged(): void {
  if (typeof window === 'undefined' || onAdminPage()) return;
  // The pageview being left is over: its page_leave goes out before the new
  // page's clocks start (and under the OLD page's path).
  queuePageLeave();
  startPageVisit();
  stopEngagedTimer();
  engagedRemainingMs = ENGAGED_AFTER_MS;
  engagedArmedAt = Date.now();
  if (!engagedListening) {
    engagedListening = true;
    document.addEventListener('visibilitychange', onPageVisibilityChange);
  }
  resumeEngagedTimer();
}

/**
 * Close the page: cancel a pending marker and queue the open pageview's
 * page_leave (uncategorised route, 404, admin — and App.tsx calls it ahead of
 * every key_page_view, so a page's report precedes the next page's pageview).
 * The uncategorised page gets no clock of its own; on the admin panel the
 * report is dropped, never sent.
 */
export function disarmPageEngaged(): void {
  stopEngagedTimer();
  engagedRemainingMs = 0;
  queuePageLeave();
  pageVisit = null;
}

/**
 * Best-effort beacon to the site's own outbound counter. sendBeacon survives
 * the page unload that an outbound navigation can trigger; the fetch fallback
 * uses keepalive for the same reason. Must never break the click.
 */
function sendOutboundPing(data: { u: string; d: string; s: string; l: string }): void {
  try {
    const body = JSON.stringify(data);
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // false = UA refused to queue (beacon quota) — fall through to fetch.
      if (
        navigator.sendBeacon('/api/outbound-ping', new Blob([body], { type: 'application/json' }))
      ) {
        return;
      }
    }
    void fetch('/api/outbound-ping', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* tracking must never break navigation */
  }
}

/**
 * Global outbound-click tracker: ONE capture-phase listener that fires
 * `outbound_click` for every anchor leaving the site — including links inside
 * CMS rich text (dangerouslySetInnerHTML), which can never get per-link
 * onClick handlers. Capture phase runs before row handlers, so CTAs that call
 * stopPropagation() are still counted.
 *
 * `outbound_click` is deliberately a superset: semantic events
 * (provider_signup_click, installer_outbound_click) keep firing from their own
 * handlers alongside it. That makes one event —
 * `outbound_click`, with `link_domain` — the complete ranked answer to
 * "which sites do visitors exit to", while the semantic events keep carrying
 * provider identity, rank, and funnel context.
 *
 * Query strings are dropped from link_url (pure UTM noise on our own CTAs);
 * link_domain is the aggregation key and link_text is what the user clicked.
 * Admin exclusion comes free via the trackEvent-level guard.
 *
 * It also installs the first-party stream's page-lifecycle listeners: the
 * flush on tab hide / pagehide (with the open visit's page_leave queued ahead
 * of it), the back/forward-cache pageshow, and the scroll + height sampling
 * behind page_leave's depth.
 */
export function initOutboundClickTracking(): () => void {
  if (typeof document === 'undefined') return () => {};

  const handler = (e: MouseEvent): void => {
    // 'click' fires for the primary button; 'auxclick' with button 1 catches
    // middle-click "open in new tab". Other aux buttons are not navigations.
    if (e.type === 'auxclick' && e.button !== 1) return;
    if (!(e.target instanceof Element)) return;
    const anchor = e.target.closest('a[href]');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href) return;

    let url: URL;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return; // malformed href
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
    // www-insensitive: an absolute link to https://www.ere-vergelijk.nl/… in
    // CMS copy 301s straight back to the apex — that is not an exit and must
    // not pollute the outbound tallies with our own domain.
    const stripWww = (h: string): string => h.replace(/^www\./, '');
    if (stripWww(url.hostname) === stripWww(window.location.hostname)) return;

    const { language, path } = splitLanguageFromPath(window.location.pathname);
    const linkText = (anchor.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 60);
    const linkUrl = `${url.origin}${url.pathname}`.slice(0, 100);
    const surface = pageCategoryForPath(path) ?? 'other';

    trackEvent('outbound_click', {
      link_url: linkUrl,
      link_domain: url.hostname,
      link_text: linkText || (anchor.getAttribute('aria-label') ?? '').slice(0, 60),
      surface,
      language,
    });

    // Third sink: first-party tally behind the admin-dashboard "Uitgaande
    // kliks" widget. Anonymous and cookieless, so it needs no consent gate —
    // and unlike GA4 (consent-gated) it counts every click. trackEvent's
    // admin guard doesn't cover this call, so it gets its own.
    if (!onAdminPage()) {
      sendOutboundPing({ u: linkUrl, d: url.hostname, s: surface, l: language });
    }
  };

  // Flush the event batch when the page is being left/hidden — sendBeacon
  // inside flushEventQueue survives the unload. The open visit's page_leave is
  // queued FIRST, so it rides that flush — for a closing tab the last one
  // there is — instead of waiting for a timer a closing tab never runs. Hidden
  // fires before pagehide on a normal unload; an unchanged page_leave is not
  // queued twice.
  const onPageHide = (): void => {
    queuePageLeave();
    // Off screen until a pageshow brings it back from the back/forward cache.
    pauseVisitClock();
    flushEventQueue();
  };
  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      queuePageLeave();
      flushEventQueue();
    }
  };
  // Restored from the back/forward cache: the same visit, on screen again.
  // No key_page_view fires for it, so it is resumed, not re-armed.
  const onPageShow = (e: PageTransitionEvent): void => {
    if (e.persisted) resumeVisitClock();
  };
  // page_leave's scroll depth: one max per scroll event, no height read.
  const onScroll = (): void => sampleVisitReach();
  // ...and the page's height, re-read after layout whenever it changes, so an
  // SPA leave can use the height of the page it closes, not the next one's.
  const heightObserver =
    typeof ResizeObserver === 'function' ? new ResizeObserver(() => sampleVisitHeight()) : null;

  document.addEventListener('click', handler, true);
  document.addEventListener('auxclick', handler, true);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('scroll', onScroll, { passive: true });
  heightObserver?.observe(document.documentElement);
  return () => {
    document.removeEventListener('click', handler, true);
    document.removeEventListener('auxclick', handler, true);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('pageshow', onPageShow);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('scroll', onScroll);
    heightObserver?.disconnect();
  };
}

/**
 * Map a language-stripped pathname to a `key_page_view` page_category. Returns
 * null for routes we deliberately don't categorise (redirects, admin, and the
 * 404 — which fires its own `not_found_view`). Pure + SSR-safe so it can be
 * shared by the client router and any future server-side use.
 */
export function pageCategoryForPath(path: string): string | null {
  if (path === '/') return 'home';
  if (path === '/kennisbank') return 'knowledge_base';
  if (path.startsWith('/kennisbank/')) return 'article';
  if (path === '/about') return 'about';
  if (path === '/begrippen') return 'glossary';
  if (path.startsWith('/begrippen/')) return 'glossary_term';
  if (path.startsWith('/auteur/')) return 'author';
  if (path === '/ere-prijs') return 'ere_price';
  if (path === '/hoe-wij-vergelijken') return 'how_we_compare';
  if (path === '/contact') return 'contact';
  if (path === '/mid-check') return 'mid_check';
  if (path === '/laadpalen') return 'charger_brands';
  if (path.startsWith('/laadpalen/')) return 'charger_brand';
  // The hub and the addresses of its retired province pages are one funnel:
  // one category, so the older province rows (the pages went on 2026-10-07)
  // count with the hub, and a province address the app still renders before
  // its hop to the hub files no other page.
  if (path === '/geen-laadpaal' || path.startsWith('/geen-laadpaal/')) return 'no_charger_landing';
  if (path === '/zakelijk') return 'zakelijk';
  if (path === '/vergelijk') return 'multi_compare';
  if (path.startsWith('/vergelijk/')) return 'head_to_head';
  if (path === `/${PROVIDERS_SEGMENT}`) return 'provider_list';
  if (path.startsWith(`/${PROVIDERS_SEGMENT}/`)) return 'provider_profile';
  if (path === '/partner') return 'partner_landing';
  if (path === '/partner-login') return 'partner_login';
  if (path === '/partner-portal') return 'partner_portal';
  if (path.startsWith('/partner-invite/')) return 'partner_invite';
  if (path === '/partner-forgot') return 'partner_forgot';
  if (path.startsWith('/partner-reset/')) return 'partner_reset';
  if (path === '/aanmelden/bevestigd') return 'signup_confirmed';
  if (path === '/privacy' || path === '/terms' || path === '/cookies' || path === '/disclaimer') {
    return 'legal';
  }
  return null;
}

/**
 * A lead landing while it still carries its token — the signup confirm page
 * (`/{lang}/aanmelden/bevestigd?id=…&token=…`) or its twin, the
 * overstapherinnering's unsubscribe page (`/{lang}/aanmelden/afgemeld?…`,
 * 2026-10-14), no `status` yet. That load POSTs and then reloads itself as
 * `?status=…` (SignupConfirmPage, SignupUnsubscribePage) — the reload is the
 * page view. Counting both would double every visit, and the token must never
 * reach a vendor, so the token-bearing load fires no page view at all; it is
 * also the load the server strips GA4 from (server/spaRenderer.ts
 * isAnalyticsFreeRequest). Pure + SSR-safe.
 */
export function isTokenConfirmLoad(pathname: string, search: string): boolean {
  if (!/\/aanmelden\/(bevestigd|afgemeld)\/?$/.test(pathname)) return false;
  const params = new URLSearchParams(search);
  return params.has('token') && !params.has('status');
}
